/**
 * Traits resource (CT-5715): what Ori learned about a person from conversations,
 * listed on the Intelligence tab and opened one at a time in a slide-out.
 *
 * No contract has been shared yet, so every path below is ASSUMED and marked
 * `TODO(CT-5715 / backend)` — reconcile at cutover. Real signatures and
 * snake_case bodies; only the transport is stubbed (`mockTransport.js`).
 *
 * The list narrows on the server: search, the quick-filter chip, the All filters
 * selections and the named sort all travel in the body. Nothing filters locally.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { mapHistoryEntries } from '../shared/utils/historyEntries.js';
import { request } from './mockTransport.js';

export {
  __resetMockIntelligenceForTests,
  __setMockIntelligenceScenario,
} from './__mocks__/intelligence.mock.js';

const numberOrNull = (value) => (typeof value === 'number' ? value : null);

/**
 * Map one trait row — verified or not — onto what the card renders. Optional
 * fields are `null`, never `''`, so a slot with nothing renders nothing.
 */
export const mapApiTraitToUi = (raw = {}) => ({
  id: raw.id ?? raw.trait_id ?? raw.traitId ?? '',
  title: raw.title ?? '',
  category: raw.category ?? null,
  sourceKind: raw.source_kind ?? raw.sourceKind ?? null,
  quote: raw.quote ?? null,
  sourceLine: raw.source_line ?? raw.sourceLine ?? null,
  expiresAt: raw.expires_at ?? raw.expiresAt ?? null,
  status: raw.status ?? null,
  usedIn: raw.used_in ?? raw.usedIn ?? [],
  note: raw.note ?? null,
  confidence: numberOrNull(raw.confidence),
  disappearsInDays: numberOrNull(raw.disappears_in_days ?? raw.disappearsInDays),
});

const mapSource = (raw) =>
  raw
    ? {
        kind: raw.kind ?? null,
        label: raw.label ?? '',
        author: raw.author ?? null,
        at: raw.at ?? raw.occurred_at ?? null,
      }
    : null;

/**
 * Map one trait's detail onto what the slide-out renders. `reportOpen` defaults
 * to false: no report is known until the API says so.
 */
export const mapApiTraitDetailToUi = (raw = {}) => ({
  ...mapApiTraitToUi(raw),
  label: raw.label ?? null,
  value: raw.value ?? null,
  summary: raw.summary ?? null,
  since: raw.since ?? raw.evidence_at ?? raw.evidenceAt ?? raw.source?.at ?? null,
  source: mapSource(raw.source),
  visibility: raw.visibility ?? null,
  history: mapHistoryEntries(raw.history),
  unverified: Boolean(raw.unverified),
  reportOpen: Boolean(raw.report_open ?? raw.reportOpen ?? false),
});

/**
 * TODO(CT-5715 / backend): `POST /v0/profile/traits/list` with `{ entity_type,
 * entity_id, quick_filter, page, page_size, order_by, order_direction,
 * filters: { source, status, used_in }, search: { query, fields } }`.
 */
export const listTraits = async ({
  entityType,
  entityId,
  quickFilter = 'all',
  page = 1,
  pageSize = 10,
  orderBy = 'needs_attention',
  orderDirection = 'asc',
  filters,
  search,
} = {}) => {
  const response = await request('/v0/profile/traits/list', {
    entity_type: entityType,
    entity_id: entityId,
    quick_filter: quickFilter,
    page,
    page_size: pageSize,
    order_by: orderBy,
    order_direction: orderDirection,
    ...(filters ? { filters } : {}),
    ...(search ? { search } : {}),
  });
  return validateApiResponse(response, 'Failed to load the traits');
};

/** TODO(CT-5715 / backend): `POST /v0/profile/traits/get` with `{ entity_type, entity_id, trait_id }`. */
export const getTrait = async ({ entityType, entityId, traitId } = {}) => {
  const response = await request('/v0/profile/traits/get', {
    entity_type: entityType,
    entity_id: entityId,
    trait_id: traitId,
  });
  return validateApiResponse(response, 'Failed to load the trait');
};

/** TODO(CT-5715 / backend): `POST /v0/profile/traits/report` with `{ entity_type, entity_id, trait_id }`. */
export const reportTraitIssue = async ({ entityType, entityId, traitId } = {}) => {
  const response = await request('/v0/profile/traits/report', {
    entity_type: entityType,
    entity_id: entityId,
    trait_id: traitId,
  });
  return validateApiResponse(response, 'Failed to report the issue');
};
